import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import React, {lazy, useCallback, useEffect, useState} from 'react'
import {usePlaygroundManager} from '../../../utils/playground-manager'
import {Box, Button, IconButton} from '@primer/react'
import {PlaygroundChatInput} from './PlaygroundChatInput'
import {AlertIcon, PencilIcon} from '@primer/octicons-react'
import type {ModelState} from '../../../types'
import {ModelLegalTerms} from './GettingStartedDialog/ModelLegalTerms'
import {isValidPlaygroundMessageJSON, validateJSONAsPlaygroundMessages} from './playground-message-utils'
const CodeMirror = lazy(() => import('@github-ui/code-mirror'))

export type PlaygroundJSONProps = {
  model: ModelState
  position: number
  setToolbarContent: React.Dispatch<React.SetStateAction<React.ReactNode>>
  setFullWidthToolbarContent: React.Dispatch<React.SetStateAction<React.ReactNode>>
  stopStreamingMessages: () => void
  handleSendChatMessage: (message: string, attachments: string[]) => void
}

export function PlaygroundJSON({
  model,
  position,
  setToolbarContent,
  setFullWidthToolbarContent,
  stopStreamingMessages,
  handleSendChatMessage,
}: PlaygroundJSONProps) {
  const [initialContent, setInitialContent] = useState<string>(() => JSON.stringify(model.messages, null, 2))
  const [content, setContent] = useState<string>(initialContent)
  const [isValidJSON, setIsValidJSON] = useState<boolean>(true)
  const [editMode, setEditMode] = useState<boolean>(false)

  const manager = usePlaygroundManager()

  const onChange = (input: string) => {
    setContent(input)
    setIsValidJSON(isValidPlaygroundMessageJSON(input))
  }

  const renderToolbar = useCallback(() => {
    const applyInput = (input: string) => {
      if (!isValidPlaygroundMessageJSON(input)) {
        return
      }

      const parsed = validateJSONAsPlaygroundMessages(input)
      if (parsed) {
        manager.setMessages(position, parsed)
        setInitialContent(input)
        setEditMode(false)
      }
    }

    const resetInput = () => {
      setContent(initialContent)
      setIsValidJSON(true)
      setEditMode(false)
    }

    const doEdit = () => {
      stopStreamingMessages()
      setEditMode(true)
    }

    if (!editMode) {
      return (
        <>
          <IconButton
            sx={{display: ['flex', 'none', 'none']}}
            size="small"
            icon={PencilIcon}
            aria-label="Edit JSON"
            onClick={doEdit}
          />
          <Button sx={{display: ['none', 'flex', 'flex']}} onClick={doEdit} size="small">
            Edit
          </Button>
        </>
      )
    } else {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            gap: 2,
          }}
        >
          <>
            <span className="color-fg-danger">
              {!isValidJSON && (
                <>
                  <AlertIcon aria-hidden /> Invalid JSON or value
                </>
              )}
            </span>
          </>
          <>
            <Button onClick={() => resetInput()} size="small">
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => applyInput(content)}
              size="small"
              disabled={content === initialContent || !isValidJSON}
            >
              Apply changes
            </Button>
          </>
        </Box>
      )
    }
  }, [content, editMode, initialContent, isValidJSON, manager, position, stopStreamingMessages])

  useEffect(() => {
    setFullWidthToolbarContent(editMode ? renderToolbar() : null)
    setToolbarContent(!editMode ? renderToolbar() : null)

    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, initialContent, isValidJSON, editMode])

  // Important if last session is restored by the user
  useEffect(() => {
    const _content = JSON.stringify(model.messages, null, 2)
    setInitialContent(_content)
    setContent(_content)
  }, [model.messages])

  return (
    <div className="pl-3 pb-2 d-flex position-relative flex-column overflow-auto flex-1">
      <CopyToClipboardButton
        className="position-absolute"
        sx={{right: '8px', top: '8px', zIndex: 10}}
        textToCopy={initialContent}
        ariaLabel={'Copy to clipboard'}
      />

      <div className="height-full overflow-auto">
        <React.Suspense fallback={<></>}>
          <CodeMirror
            fileName={`document.json`}
            value={content}
            ariaLabelledBy={''}
            height="100%"
            spacing={{
              indentUnit: 2,
              indentWithTabs: false,
              lineWrapping: true,
            }}
            hideHelpUntilFocus
            onChange={onChange}
            isReadOnly={!editMode || model.isLoading}
          />
        </React.Suspense>
      </div>

      {!editMode && (
        <>
          <PlaygroundChatInput
            model={model}
            position={position}
            stopStreamingMessages={stopStreamingMessages}
            handleSendChatMessage={handleSendChatMessage}
          />
          <div className="pt-3 pb-2 d-flex flex-justify-center pr-3">
            <ModelLegalTerms modelName={model.catalogData.name} />
          </div>
        </>
      )}
    </div>
  )
}

try{ CodeMirror.displayName ||= 'CodeMirror' } catch {}
try{ PlaygroundJSON.displayName ||= 'PlaygroundJSON' } catch {}